import { Abs, Max, Min, PI, Sign, POS_INFINITY } from "@pdfking/math"
import { NullQ } from "@pdfking/structs"

import type { FoldCell, FoldRig, Vec3 } from "#public"

/**
 * How the cells of a sheet `depth` thick turn: each on the edge its box
 * shares with its parent's — known from the edge alone, so a cell with no
 * width still turns the right way —, on the face it folds onto, past the
 * layers that come between once folded: its siblings folded the same way
 * and earlier, and what is folded onto the face it turns onto, each with
 * all folded on it. A cell turning less than a quarter lies beside its
 * parent, never on it; cells off different edges lie side by side, the
 * thickest edge's the stack — a board's turn-ins one layer, not four. The
 * axis turns the cell onto the sheet's rear for a positive angle. Pure: the
 * viewer rigs its bones on it, an icon its moves (FoldMoves).
 */
export const CreateFoldRig = (folds: FoldCell[], depth: number, eps: number): FoldRig => {
  const near = (a: number, b: number) => Abs(a - b) <= eps
  const out = folds.map((fold): [number, number] => {
    if (NullQ(fold.parent)) return [0, 0]
    const parent = folds[fold.parent]!.box
    const { box } = fold
    if (near(box.x, parent.x + parent.width)) return [1, 0]
    if (near(box.x + box.width, parent.x)) return [-1, 0]
    if (near(box.y, parent.y + parent.height)) return [0, 1]
    return [0, -1]
  })

  const children = (i: number) => folds.flatMap((fold, c) => (fold.parent === i ? [c] : []))
  const onto = (c: number) => Abs(folds[c]!.angle) >= PI / 2 - 1e-6
  const stack = (cells: readonly number[]): number => {
    const edges = new Map<string, number>()
    for (const c of cells) {
      const key = out[c]!.join()
      edges.set(key, (edges.get(key) ?? 0) + size(c))
    }
    return Max(0, ...edges.values())
  }
  const size = (i: number): number => 1 + stack(children(i).filter(onto))
  const same = (a: number, b: number) => Sign(folds[a]!.angle) === Sign(folds[b]!.angle)
  // A step 0 fold never opens: folded before any other, the innermost
  const order = (c: number) => folds[c]!.step || POS_INFINITY
  const layers = (i: number) =>
    stack(
      children(folds[i]!.parent!).filter(
        c => c !== i && onto(c) && same(c, i) && order(c) > order(i)
      )
    ) + stack(children(i).filter(c => onto(c) && same(c, i)))

  const pivots = folds.map((fold, i): Vec3 => {
    const { box } = fold
    if (NullQ(fold.parent)) return [box.x + box.width / 2, box.y + box.height / 2, 0]
    const parent = folds[fold.parent]!.box
    const z = -Sign(fold.angle) * (depth / 2 + layers(i) * depth)
    const mid_y = (Max(box.y, parent.y) + Min(box.y + box.height, parent.y + parent.height)) / 2
    const mid_x = (Max(box.x, parent.x) + Min(box.x + box.width, parent.x + parent.width)) / 2
    const [dx, dy] = out[i]!
    if (dx > 0) return [box.x, mid_y, z]
    if (dx < 0) return [parent.x, mid_y, z]
    if (dy > 0) return [mid_x, box.y, z]
    return [mid_x, parent.y, z]
  })
  const axes = folds.map((fold, i): Vec3 => {
    if (NullQ(fold.parent)) return [0, 0, 1]
    const [dx, dy] = out[i]!
    return dx ? [0, dx, 0] : [-dy, 0, 0]
  })
  return { out, pivots, axes }
}
